import axios from "axios";
import { WorkBook, WorkSheet } from "xlsx";
import { TemplateConfig, TypeTemplate } from "../config";
import { StorageAuth } from "../core/localStorage";
import { IColumnInfo, TypeColumnValue, TypeValueValidation } from "../models";
import { UtilityDate } from "./dateUtils";
import { UtilityValidator } from "./validatorUtils";
import { UtilityWorkBook } from "./workbookUtils";
import { AppRouteApi } from "../config/appRoute";
import { ColumRequired } from "../config/template/columns";

export interface IConvertValue {
  cellValue: any;
  cellWord: string;
  columnValue: any;
  valueType: TypeColumnValue;
}
export interface IValidateValue {
  cellValue: any;
  cellWord: string;
  columnInfo: IColumnInfo;
  uniqueFields: { [key: string]: any[] };
}
type TypeErrorKey = keyof typeof TemplateConfig.Message.error;

function convertValue({
  valueType,
  cellValue,
  cellWord,
  columnValue,
}: IConvertValue) {
  const typeOfValue = typeof cellValue;
  if (typeOfValue === valueType) return cellValue;

  switch (valueType) {
    case "day":
      return cellValue === "Yes" ? columnValue : "";
    case "number":
      return Number(cellValue);
    case "string":
      return String(cellValue);
    case "boolean":
      return cellValue === "Yes" ? true : false;
    case "date":
      return UtilityDate.ToIsoUtcDate(new Date(cellWord));
    case "date-time":
      return UtilityDate.addTimeToDate(cellWord).toISOString();
    case "array":
      return typeOfValue === "string" ? cellValue.split(",") : [cellValue];
    default:
      return cellValue;
  }
}

const convertDataToJSON = async (
  workSheet: WorkSheet,
  templateType: TypeTemplate,
  haveToConvertType: boolean
) => {
  if (!workSheet) {
    return;
  }

  const data = await UtilityWorkBook.convertToJSON(
    workSheet,
    templateType,
    haveToConvertType
  );

  return data;
};

const convertDataToJSONForApi = async (
  workbook: WorkBook,
  templateType: TypeTemplate,
  haveToConvertType: boolean
) => {
  const worksheet = await UtilityWorkBook.getFirstWorkSheet(workbook);
  const addressPromises: Promise<any>[] = [];

  const apiData = UtilityWorkBook.convertToJSON(
    worksheet,
    templateType!,
    haveToConvertType
  );

  if (!haveToConvertType) {
    return apiData;
  }

  for (const data of apiData) {
    data.companyId = StorageAuth.CompanyId;
    data.languages = ["English"];

    try {
      const promise = axios
        .get(AppRouteApi.ThirdParty.GeoCode(data.address))
        .then((response) => {
          const result = response.data?.results;
          if (result?.[0]) {
            data.address = result?.[0]?.formatted_address;
          }
        });
      addressPromises.push(promise);
    } catch (error) {
      console.error(error);
    }

    if (templateType !== "Caregiver") {
      continue;
    }

    data.weeklyAvailabilities = [];

    if (data?.allTimeAvailability) {
      data.availableFrom = UtilityDate.getMinimumPossibleHour(
        new Date()
      ).toISOString();
      data.availableTo = UtilityDate.getMaxPossibleHour(
        new Date()
      ).toISOString();
    }
    data.saturday && data.weeklyAvailabilities.push(data.saturday);
    data.sunday && data.weeklyAvailabilities.push(data.sunday);
    data.monday && data.weeklyAvailabilities.push(data.monday);
    data.tuesday && data.weeklyAvailabilities.push(data.tuesday);
    data.wednesday && data.weeklyAvailabilities.push(data.wednesday);
    data.thursday && data.weeklyAvailabilities.push(data.thursday);
    data.friday && data.weeklyAvailabilities.push(data.friday);

    delete data.allTimeAvailability;
    delete data.saturday;
    delete data.sunday;
    delete data.monday;
    delete data.tuesday;
    delete data.wednesday;
    delete data.thursday;
    delete data.friday;
  }

  return new Promise((resolve) => {
    resolve(
      Promise.all(addressPromises).then(() => {
        return apiData;
      })
    );
  });
};

function validateValue({
  cellValue,
  cellWord,
  validationType,
  columnInfo,
  uniqueFields,
}: Omit<IValidateValue, "validationTypes"> & {
  validationType: TypeValueValidation;
}) {
  switch (validationType) {
    case "empty":
      return !!cellWord;
    case "password":
      return cellWord.length >= 6;
    case "email":
      return UtilityValidator.email(cellWord);
    case "phone_number":
      return UtilityValidator.USAMobileNumber(cellWord);
    case "date":
      return UtilityValidator.date(cellWord);
    case "time":
      return UtilityValidator.time(cellWord);
    case "number":
      return (
        typeof cellValue === "number" ||
        UtilityValidator.positiveNumber(cellValue)
      );
    case "gender":
      return (
        cellValue === "Male" || cellValue === "Female" || cellValue === "Others"
      );
    case "yes_no":
      return cellWord === "Yes" || cellWord === "No";
    case "unique":
      return !uniqueFields[columnInfo.key]?.includes(cellWord);
    case "insurance_id":
      const insuranceTypes = JSON.parse(
        window.sessionStorage.getItem("insuranceTypes") || "[]"
      ) as string[];
      return insuranceTypes.includes(cellWord);
    case "address":
      return UtilityValidator.address(cellWord);
    default:
      return true;
  }
}

function getValidationStatus({
  cellValue,
  cellWord,
  columnInfo,
  uniqueFields,
}: IValidateValue) {
  const rowErrors = [];
  let haveRowError = false;
  const { validationTypes, key } = columnInfo;
  for (const validationType of validationTypes) {
    if (
      !validateValue({
        cellValue,
        cellWord,
        columnInfo,
        validationType,
        uniqueFields,
      })
    ) {
      rowErrors.push(
        TemplateConfig.Message.error[validationType as TypeErrorKey]
      );
      haveRowError = true;
      continue;
    }
    if (validationType === "unique") {
      uniqueFields[key]
        ? uniqueFields[key].push(cellWord)
        : (uniqueFields[key] = [cellWord]);
    }
  }

  return { rowErrors, haveRowError };
}

function getMissingRequiredValueStatus({
  errors,
  data,
  templateType,
}: {
  errors: { [key: string]: any }[];
  data: { [key: string]: any }[];
  templateType: TypeTemplate;
}) {
  let haveRequiredValueError = false;
  const updatedErrors = errors;

  const requiredColumns = (() => {
    switch (templateType) {
      case "Caregiver":
        return ColumRequired.caregiver;
      case "Patient":
        return ColumRequired.patient;
      default:
        return [];
    }
  })();

  if (!requiredColumns.length) {
    return { updatedErrors, haveRequiredValueError };
  }

  for (let index = 0; index < data.length; index++) {
    const item = data[index];
    for (const column of requiredColumns) {
      if (!item[column.key]) {
        if (!errors[index][column.key]) {
          errors[index][column.key] = [];
        }
        haveRequiredValueError = true;
        errors[index][column.key].push(TemplateConfig.Message.error["empty"]);
      }

      if (templateType !== "Caregiver") {
        continue;
      }
      if (
        column.key === "allTimeAvailability" &&
        item["allTimeAvailability"] !== "Yes"
      ) {
        if (!item["availableFrom"]) {
          if (!errors[index]["availableFrom"]) {
            errors[index]["availableFrom"] = [];
          }
          errors[index]["availableFrom"].push(
            TemplateConfig.Message.error["empty"]
          );
          haveRequiredValueError = true;
        }
        if (!item["availableTo"]) {
          if (!errors[index]["availableTo"]) {
            errors[index]["availableTo"] = [];
          }
          errors[index]["availableTo"].push(
            TemplateConfig.Message.error["empty"]
          );
          haveRequiredValueError = true;
        }
      }
    }
  }

  return { updatedErrors, haveRequiredValueError };
}

export const UtilityTemplate = {
  convertValue,
  convertDataToJSONForApi,
  convertDataToJSON,
  getValidationStatus,
  getMissingRequiredValueStatus,
};
